import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Play, ExternalLink, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";

interface Run {
  id: string;
  source: string;
  keyword: string | null;
  metrics: any;
  status: string;
  blog_post_id: string | null;
  error: string | null;
  created_at: string;
}

const statusColor: Record<string, string> = {
  draft_created: "bg-green-100 text-green-700",
  running: "bg-blue-100 text-blue-700",
  error: "bg-red-100 text-red-700",
  no_keyword: "bg-yellow-100 text-yellow-700",
  skipped: "bg-gray-100 text-gray-700",
  pending: "bg-gray-100 text-gray-700",
};

const AdminBlogAutomation = () => {
  const qc = useQueryClient();
  const [running, setRunning] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const { data: runs = [] } = useQuery({
    queryKey: ["blog-automation-runs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_automation_runs" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data as unknown as Run[];
    },
  });

  const { data: settings = {} } = useQuery({
    queryKey: ["site-settings-auto-blog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("key,value")
        .like("key", "auto_blog%");
      if (error) throw error;
      return Object.fromEntries((data || []).map((r: any) => [r.key, r.value])) as Record<string, string>;
    },
  });

  const updateSetting = async (key: string, value: string) => {
    setSavingSettings(true);
    const { error } = await supabase.from("site_settings").upsert({ key, value, updated_at: new Date().toISOString() });
    setSavingSettings(false);
    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["site-settings-auto-blog"] });
    }
  };

  const handleRunNow = async () => {
    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("auto-blog-pipeline");
      if (error) throw error;
      if (data?.success) {
        toast.success(`Draft created: ${data.post?.title || "(untitled)"}`);
      } else if (data?.skipped) {
        toast.info("Skipped — automation disabled");
      } else if (data?.reason === "no_keyword") {
        toast.warning("No new niche-matching queries available in GSC right now");
      } else {
        toast.error(data?.error || "Pipeline failed");
      }
      qc.invalidateQueries({ queryKey: ["blog-automation-runs"] });
    } catch (e: any) {
      toast.error(e.message || "Failed to run pipeline");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Blog Automation</h1>
          <p className="text-sm text-muted-foreground font-body">
            Pulls top-impression queries from Google Search Console twice weekly (Mon &amp; Thu, 09:00 UTC), drafts a blog post with cover image, and emails you for approval.
          </p>
        </div>
        <Button onClick={handleRunNow} disabled={running}>
          {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
          Run now
        </Button>
      </div>

      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <h2 className="font-display text-lg font-semibold">Settings</h2>
        <div className="flex items-center justify-between">
          <div>
            <Label className="font-body">Automation enabled</Label>
            <p className="text-xs text-muted-foreground">Master switch for scheduled runs</p>
          </div>
          <Switch
            checked={settings.auto_blog_enabled === "true"}
            disabled={savingSettings}
            onCheckedChange={(v) => updateSetting("auto_blog_enabled", v ? "true" : "false")}
          />
        </div>
        <div className="space-y-2">
          <Label className="font-body">Approval notification email</Label>
          <Input
            defaultValue={settings.auto_blog_notification_email || ""}
            onBlur={(e) => {
              if (e.target.value !== settings.auto_blog_notification_email) {
                updateSetting("auto_blog_notification_email", e.target.value);
              }
            }}
          />
        </div>
        <div className="space-y-2">
          <Label className="font-body">GSC site URL</Label>
          <Input
            defaultValue={settings.auto_blog_gsc_site_url || ""}
            placeholder="sc-domain:plymhair.co.uk"
            onBlur={(e) => {
              if (e.target.value !== settings.auto_blog_gsc_site_url) {
                updateSetting("auto_blog_gsc_site_url", e.target.value);
              }
            }}
          />
          <p className="text-xs text-muted-foreground">Use <code>sc-domain:yourdomain.com</code> for domain properties.</p>
        </div>
        <div className="space-y-2">
          <Label className="font-body">Niche seed keywords (comma-separated)</Label>
          <Input
            defaultValue={settings.auto_blog_seed_keywords || ""}
            onBlur={(e) => {
              if (e.target.value !== settings.auto_blog_seed_keywords) {
                updateSetting("auto_blog_seed_keywords", e.target.value);
              }
            }}
          />
          <p className="text-xs text-muted-foreground">Only GSC queries containing one of these terms become blog topics.</p>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg overflow-hidden">
        <div className="p-4 border-b border-border">
          <h2 className="font-display text-lg font-semibold">Recent runs</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>When</TableHead>
              <TableHead>Keyword</TableHead>
              <TableHead>Impressions</TableHead>
              <TableHead>Position</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Draft</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {runs.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No runs yet. Click <strong>Run now</strong> to generate the first draft.
                </TableCell>
              </TableRow>
            )}
            {runs.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="text-xs">{format(new Date(r.created_at), "dd MMM yyyy HH:mm")}</TableCell>
                <TableCell className="font-body max-w-xs truncate">{r.keyword || "—"}</TableCell>
                <TableCell>{r.metrics?.impressions ?? "—"}</TableCell>
                <TableCell>{r.metrics?.position ? Number(r.metrics.position).toFixed(1) : "—"}</TableCell>
                <TableCell>
                  <Badge className={statusColor[r.status] || ""} variant="secondary">{r.status}</Badge>
                  {r.error && <p className="text-xs text-red-600 mt-1 truncate max-w-xs" title={r.error}>{r.error}</p>}
                </TableCell>
                <TableCell>
                  {r.blog_post_id ? (
                    <Link to="/admin/blog" className="text-primary hover:underline inline-flex items-center gap-1 text-sm">
                      Open <ExternalLink className="w-3 h-3" />
                    </Link>
                  ) : "—"}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default AdminBlogAutomation;
